import { Bind, Debounce } from '@/utils/Decorator'
import {
  Button,
  Empty,
  Icon,
  Select
} from '@/plugins/ant-design-vue/tsx-support'
import {
  CommonLoading,
  CommonRewardTip,
  CommonTabs
} from '@/components/business-components'
import { Component, Ref, Watch } from '@/vue-property-decorator'
import { EmptyImageReport } from '@/components/business-components/empty-image'
import { GeetestValidateResult } from '@/controller/GeeTest'
import { GlobalConst, GlobalEvents, GlobalManager } from '@/context'
import { PayModalConfigManager } from './config'
import { RewardBindType } from '@/api/common/type'
import { RootCategoryEnum } from './store/type'
import { RouteName } from '@/router/config'
import { apiCenterGetAccountStatistics } from '@/api/center/report'
import { createSvgSprite } from '@/utils/business-utils/svg'
import { currencyFormatter } from '@/utils/business-utils/finance'
import { getCurrentDevice } from '@/utils/Device'
import { merge } from 'lodash'
import { onAppEvent } from '@/utils/business-utils'
import { showForceChangeWidthdrawPasswordDialog } from '@/views/finance/withdraw/utils'
import { useI18n } from '@/i18n'
import { useMainStore } from '@/store/index'
import BusinessUtils from '@/utils/business-utils'
import Manager from '@/context/manager'
import Modal from '@/controller/Modal'
import ModalBase from '@/mixins/business/modal/modal-base'
import NetRetry, {
  DisplayType
} from '@/components/business-components/net-retry'
import OnlinePayComp from './components/online'
import PayDetailsModalComp from './components/detail'
import Render from '@/components/business-components/func-render/async-component'
import TooltipsComp from '@/components/business-components/tooltips'
import TransferComp from './components/transfer'
import Validator from '@/utils/Validator'
import router from '@/router'
import style from './style.module.scss'
import to from 'await-to-js'
import useFinancePayStore from './store'
import useGoubaoPayStore from './store/goubao'

/**
 * (1-在线充值，2-转账，3-虚拟货币，4-人工充值)
 */

type State = {
  submitLoading: boolean
}
@Component<PayModal>({
  name: 'PayModal',
  components: {
    OnlinePayComp: Render.createAsyncComponent({
      component: () => import('./components/online')
    }),
    TransferComp: Render.createAsyncComponent({
      component: () => import('./components/transfer')
    }),
    ArtificialComp: Render.createAsyncComponent({
      component: () => import('./components/artificial')
    }),
    SilverMerchant: Render.createAsyncComponent({
      component: () => import('./components/silverMerchant')
    })
  }
})
export default class PayModal extends ModalBase {
  protected get transitionName() {
    return useMainStore().isWeb
      ? GlobalConst.CssClassName.Animate.TransitionName.Zoom
      : GlobalConst.CssClassName.Animate.TransitionName.MoveDown
  }
  autoresize = true

  mainStore = useMainStore()

  store = useFinancePayStore()

  state: State = {
    submitLoading: false
  }

  /**
   * i18n
   */
  t = useI18n().t
  /**
   * user account total
   */
  accountTotal = 0
  /**
   * 当前滚动元素的实体
   */
  @Ref() readonly scrollContainer!: HTMLDivElement
  /**
   * 在线充值组件的实体
   */
  @Ref() readonly onlinePayComp!: OnlinePayComp
  /**
   * 虚拟币充值组件的实体
   */
  @Ref() readonly cryptoPayComp!: OnlinePayComp
  /**
   * 转账充值组件的实体
   */
  @Ref() readonly transferComp!: TransferComp

  /**
   * 保证数据一致性
   */
  @Watch('mainStore.userInfos', { deep: true })
  updateRealName() {
    this.store.validateForm.realName = this.mainStore.userInfos?.realname
  }

  /**
   * open modal
   */
  async open() {
    if (this.mainStore.withdrawPasswordDialogVisible) {
      showForceChangeWidthdrawPasswordDialog()
      return
    }
    this.store.isPaySuccess = false

    /**
     * 在打开弹窗时，修改浏览器历史记录，以便在用户点击返回按钮时触发popstate事件而不进行路由跳转
     * IOS还有闪烁问题，暂时只上安卓
     */
    const device = getCurrentDevice()
    if (!device.ios()) {
      router.push({
        query: {
          ...this.$route.query,
          modal: 'Paymodal'
        }
      })

      // 监听物理返回按键
      window.addEventListener('popstate', this.close)
    }

    await this.baseOpen()
    GlobalEvents.dispatch({ type: 'PAYMODAL_OPEN' })
    GlobalEvents.dispatch({
      type: 'REPORT_MANAGER',
      eventName: 'ChargePageView',
      payload: {}
    })
    await Promise.all([
      this.store.initPayConfig(),
      this.store.getFastestUrlTime(),
      // this.queryAccountTotal(),
      this.mainStore.getRewardBindData()
    ])
  }

  private resetAllCompIndex() {
    this.transferComp?.resetAllIndex()
    this.onlinePayComp?.resetAllIndex()
    this.cryptoPayComp?.resetAllIndex()
  }

  /**
   * super class will call it
   */
  afterClose() {
    // this.store.$reset()
    // this.store.rootCategoryIndex = this.store.getRootCategoryListTypes[0]
    this.resetAllCompIndex()

    // 清空输入框金额
    this.store.money = ''

    // 如果是H5，并且是游戏中，充值成功需要下分并回到大厅
    const { isWeb, updatePrize } = useMainStore()
    const isInGame = this.$route.name === RouteName.GAME_EMBEDDED
    if (!isWeb && isInGame && this.store.isPaySuccess) {
      updatePrize()
      this.$router.push({ name: RouteName.GAME })
    }
    GlobalEvents.dispatch({ type: 'PAYMODAL_CLOSE' })
    const device = getCurrentDevice()
    if (!device.ios()) {
      window.removeEventListener('popstate', this.close)
    }
  }

  /**
   * tabs config
   */
  get defaultTabsConfig() {
    if (this.store.rootCategoryList.length === 0) return []
    const result = []
    const top = this.isWeb ? `-16px` : `-4px`
    for (const item of this.store.rootCategoryList) {
      const iconSource = PayModalConfigManager.getIconByType(item.type)
      result.push({
        label: (
          <div class={[style.label, style.sp]}>
            <Icon.Tsx
              component={{
                functional: true,
                render() {
                  return (
                    <icon-sprite
                      sprite={iconSource.icon}
                      attrs={{
                        color: iconSource.iconColor
                      }}
                    />
                  )
                }
              }}
            />
            <span class={style.titleName}>{item.name}</span>
            <TooltipsComp
              charge={item.charge_rate}
              class={style.tooltipText}
              top={BusinessUtils.px2rem(top)}
              right={BusinessUtils.px2rem('-10px')}
              // customStyle={{ transform: `translateX(50%)`, height: 'auto' }}
              text={this.t('lobby.modal.pay.give', {
                num: `${item.charge_rate}`
              })}
            />
          </div>
        ),
        value: item.type
      })
    }
    return result
  }

  /**
   * query account total
   */
  private async queryAccountTotal() {
    const [err, data] = await to(
      apiCenterGetAccountStatistics({
        time: '0',
        platform_id: '0',
        cate_id: '0',
        type: '0'
      })
    )
    if (err) {
      // console.error('pay modal [queryAccountTotal] error:', err)
    }
    this.accountTotal = data?.data.data?.balance ?? 0
  }

  /**
   * render tabs footer[right]
   */
  get renderTabsFooter() {
    return (
      <span class={style.tabsFooter}>
        <span>{this.t('lobby.modal.pay.accountTotal')}</span>
        <span class={style.mark}>{currencyFormatter(this.accountTotal)}</span>
      </span>
    )
  }

  /**
   * common footer
   */
  get renderFooter() {
    const {
      isQianNengWalletChannel,
      isNoWalletDirectPurchaseChannel,
      isNoWalletBalancePayChannel,
      isNoWalletBalancePayChannel2,
      hasNoWallet,
      qianNengAccountDetail,
      bobiAccountDetail,
      isBobiedWalletChannel,
      isUPAYWalletChannel,
      isBindUPAYWallet,
      isGoubaoWalletChannel
    } = this.store
    const isbindQianNeng =
      qianNengAccountDetail.data?.errcode ===
      GlobalConst.ServiceCode.SUCCESSCODE

    const isbindBobi =
      bobiAccountDetail.data.code === GlobalConst.ServiceCode.SUCCESSCODE

    const isBindUPAY = isUPAYWalletChannel && isBindUPAYWallet

    let isGoubaoWalletOK = true
    if (isGoubaoWalletChannel) {
      isGoubaoWalletOK = useGoubaoPayStore().isBound
    }

    // 四个钱包请求错误
    const isWalleteFetchError = this.store.fetchStatus.walletDetail.timeout

    // 隐藏充值按钮：no钱包直接购买、余额支付 未绑定no钱包 | 波币钱包通道 未绑定波币 | 购宝钱包-未绑定
    const hiddenBtn =
      isWalleteFetchError ||
      ((isNoWalletDirectPurchaseChannel ||
        isNoWalletBalancePayChannel ||
        isNoWalletBalancePayChannel2) &&
        !hasNoWallet) ||
      (isBobiedWalletChannel && !isbindBobi) ||
      !isGoubaoWalletOK

    if (!hiddenBtn) {
      return (
        <Button.Tsx
          type="primary"
          disabled={!this.store.signKey}
          class={style.footerButton}
          onClick={this.switchSubmit}
          loading={this.state.submitLoading}
        >
          <CommonRewardTip
            selfKey
            rewardId={RewardBindType.FirstDeposit}
            right={BusinessUtils.px2rem(0)}
            top={BusinessUtils.px2rem(`-18px`)}
          />
          {isUPAYWalletChannel && !isBindUPAYWallet
            ? this.t('lobby.modal.pay.bindUPAYBtn')
            : !isQianNengWalletChannel || isBindUPAY || isbindQianNeng
            ? this.t('lobby.modal.pay.payBtn')
            : this.t('lobby.modal.pay.bindBtn')}
        </Button.Tsx>
      )
    }
  }

  /**
   * 行为验证
   */
  async captchaValidate() {
    this.store.loading = true
    const mainStore = useMainStore()

    // 极速验证
    if (GlobalManager.User.checkGeetestRestricted()) {
      this.store.loading = false
      return Promise.reject()
    }
    const { systemInfos } = mainStore
    const captchaId = systemInfos?.geetest_captcha_id.recharge || ''
    const captchaObj = await GlobalManager.User.initGeetest({
      geetestConfig: {
        captchaId: captchaId
      }
    })
    const result: GeetestValidateResult = await new Promise(
      (resolve, reject) => {
        captchaObj
          ?.onSuccess(() => {
            resolve(captchaObj.getValidate())
          })
          .onClose(() => {
            captchaObj.destroy()
            this.store.loading = false
            reject()
          })
          .onError((errorInfo) => {
            captchaObj.destroy()
            this.store.loading = false
            reject()
            // error_12 拼图拼不上达到5次以上 error_01 刷新达到5次以上
            if (
              errorInfo.error_code == 'error_12' ||
              errorInfo.error_code == 'error_01'
            ) {
              const time = GlobalManager.Site.getTime()
              mainStore.$patch({
                geetestRestrictedTime: time
              })
              GlobalManager.User.checkGeetestRestricted()
            }
          })
      }
    )

    if (result) {
      await GlobalManager.User.validateGeetest({
        account: mainStore.userInfos?.username.toString() ?? '',
        ...merge(result, {
          captcha_id: result?.captcha_id || captchaId
        })
      })
      return true
    } else {
      this.store.loading = false
      return false
    }
  }

  /**
   * toast
   * @param content
   */
  errorToast(content: string) {
    GlobalManager.Modal.message({
      type: 'error',
      content
    })
  }

  /**
   * 充值
   */
  @Bind()
  @Debounce(800)
  async switchSubmit() {
    const {
      payWalletAddressValid,
      showRealNameInput,
      // payCaptchaValidateSwitch,
      payEmailValid,
      payPhoneValid,
      isCombination
    } = this.store.getConditionInfo
    let ref: TransferComp | OnlinePayComp | undefined = this.onlinePayComp
    switch (this.store.rootCategoryIndex) {
      case RootCategoryEnum.ONLINE:
        ref = this.onlinePayComp
        break
      case RootCategoryEnum.CRYPTO:
        ref = this.cryptoPayComp
        break
      case RootCategoryEnum.TRANSFER:
        ref = this.transferComp
        break
    }
    if (this.store.isQianNengWalletChannel) {
      const qianNengBalacne =
        this.store.qianNengAccountDetail.data?.data?.balance ?? '0'
      if (Number(this.store.money) > Number(qianNengBalacne)) {
        Modal.message({
          type: 'error',
          content: this.$t('lobby.modal.pay.toast.chargeMoneyQiannengError')
        })
        return
      }
    }
    if (this.store.isBobiedWalletChannel) {
      const bobiBalance = this.store.bobiAccountDetail.data.balance ?? '0'
      if (Number(this.store.money) > Number(bobiBalance)) {
        Modal.message({
          type: 'error',
          content: this.$t('lobby.modal.pay.toast.chargeMoneyBobiError')
        })
        return
      }
    }
    if (this.store.isUPAYWalletChannel) {
      const UPAYWalletBalacne = this.store.UPAYBalanceDetail?.balanceAmount || 0
      if (Number(this.store.money) > UPAYWalletBalacne) {
        Modal.message({
          type: 'error',
          content: this.$t('lobby.modal.pay.toast.chargeMoneyUPAYError')
        })
        return
      }
    }

    // 购宝支付密码校验
    if (this.store.isGoubaoWalletChannel) {
      if (!this.store.validateForm.walletPassword) {
        Modal.message({
          type: 'error',
          content: this.t('lobby.modal.pay.payPwdRequired')
        })
        return
      }
    }

    if (
      this.store.isQianNengWalletChannel &&
      !(
        this.store.qianNengAccountDetail?.success &&
        this.store.qianNengAccountDetail.data?.errcode ===
          GlobalConst.ServiceCode.SUCCESSCODE
      )
    ) {
      await ref?.submit()
      return
    }

    if (this.store.isUPAYWalletChannel && !this.store.isBindUPAYWallet) {
      // 创建绑定UPAY账号
      try {
        await this.store.bindUPAYWallet()
        if (this.store.isBindUPAYWallet) {
          Modal.message({
            type: 'success',
            content: this.t('lobby.modal.pay.bindUPAYSuccess')
          })
        }
      } catch (error) {
        this.errorToast(error as string)
      }
      return
    }

    // 校验金额不能为空
    if (!this.store.money) {
      this.errorToast(this.t('lobby.modal.pay.toast.moneyIncorrect').toString())
      return
    }

    // 校验金额限制
    if (this.store.checkMoneyFalse) {
      this.errorToast(this.t('lobby.modal.pay.toast.moneyIncorrect').toString())
      return
    }

    if (isCombination) {
      // 组合模式使用表单校验
      const validateResult = await ref?.validateFormRef()
      if (!validateResult) {
        return
      }
    } else {
      // 非组合模式校验
      // 金额已经填写,真实姓名未填写

      if (
        showRealNameInput &&
        this.store.money &&
        !this.store.validateForm.realName
      ) {
        GlobalManager.Modal.message({
          type: 'error',
          content: this.$t('lobby.modal.pay.realNameRequired')
        })
        return
      }

      // 金额已经填写,付款人钱包地址未填写
      if (
        payWalletAddressValid &&
        this.store.money &&
        !this.store.validateForm.walletAddress
      ) {
        GlobalManager.Modal.message({
          type: 'error',
          content: this.$t('lobby.modal.pay.walletAddressRequired')
        })
        return
      }

      // 金额已经填写,付款人手机号未填写
      if (payPhoneValid && this.store.money && !this.store.validateForm.phone) {
        GlobalManager.Modal.message({
          type: 'error',
          content: this.$t('lobby.modal.pay.personalPhoneInfoRequired')
        })
        return
      }

      // 金额已经填写,付款人邮箱未填写
      if (payEmailValid && this.store.money && !this.store.validateForm.email) {
        GlobalManager.Modal.message({
          type: 'error',
          content: this.$t('lobby.modal.pay.personalEmailInfoRequired')
        })
        return
      }
    }

    // 邮箱校验[非组合模式下]
    if (
      this.store.money &&
      this.store.validateForm.email &&
      payEmailValid &&
      !Validator.isEmail(this.store.validateForm.email)
    ) {
      GlobalManager.Modal.message({
        type: 'error',
        content: this.$t('lobby.modal.pay.personalEmailInfoError')
      })
      return
    }

    // 行为验证
    // if (payCaptchaValidateSwitch) {
    //   const [err, can] = await to(this.captchaValidate())
    //   if (err || !can) {
    //     this.store.loading = false
    //     return
    //   }
    // }

    /** 埋点 */
    onAppEvent('rechargeClick')
    GlobalEvents.dispatch({
      type: 'MONITOR_EVENT',
      eventName: 'IndicatorD',
      payload: {
        amount: this.store.money,
        currency: this.store.activeCurrencyCode
      }
    })
    /** 埋点end */

    this.setState({
      submitLoading: true
    })

    this.store.loading = true

    ref?.submit().finally(() => {
      this.store.loading = false

      // 所有页面充值按钮加3sloading效果
      setTimeout(() => {
        this.setState({
          submitLoading: false
        })
      }, 3000)
    })
  }

  /**
   * @description tabs切换事件
   */
  private async handleTrigger() {
    this.store.loading = true
    this.resetAllCompIndex()
    //  处理切换视图有残留,重置转账/在线充值通用字段
    this.store.$patch({
      activePlatformCategory: undefined,
      activePlatformMethod: undefined,
      money: undefined,
      isBindUPAYWallet: false
    })
    await this.store.getPayType(this.store.rootCategoryIndex)
    this.scrollContainer?.scrollTo(0, 0)
    this.store.isScoreMode = false
    this.store.loading = false
  }

  /**
   * @description common section wrapper
   * @param comp
   * @returns
   */
  private sectionWrapper(
    content: VueTsxSupport.JSX.Element | VueTsxSupport.JSX.Element[],
    footer?: VueTsxSupport.JSX.Element,
    noBg = false
  ) {
    const scrollContainer = noBg ? (
      content
    ) : (
      <my-scroll
        direction="y"
        onScroll={() => {
          GlobalEvents.dispatch({ type: 'ANTD_POPUP_INVISIBLE' })
        }}
      >
        {content}
        {footer && <div class={style.footer}>{footer}</div>}
      </my-scroll>
    )
    return (
      <section class={style.sectionWrapper}>
        <div class={style.container} ref={'scrollContainer'} no-bg={noBg}>
          {scrollContainer}
        </div>
      </section>
    )
  }

  /**
   * 币种分类变化
   */
  async handleCurrencyChange(category: string) {
    if (category === this.store.payCurrencyCategory) {
      return
    }
    this.store.loading = true
    this.store.payCurrencyCategory = category
    await Promise.all([this.store.initPayConfig()])
    await this.handleTrigger()
    this.store.loading = false
  }
  /**
   * 渲染头部
   */
  get renderTitle() {
    const t = this.t
    const currencyList = [''].concat(
      this.store.getPayPlatform?.payCurrency ?? []
    )
    const getIcon = PayModalConfigManager.getCurrencyIcon.bind(this)
    // 是否展示多币种选择框[3.2]版本屏蔽
    const isShowSelect = false

    return (
      <div class={style.title}>
        {isShowSelect && (
          <div class={style.selectWrapper}>
            <Select.Tsx
              class={style.select}
              optionLabelProp="customerRender"
              onChange={this.handleCurrencyChange}
              dropdownClassName={style.selectDropdown}
              value={this.store.payCurrencyCategory}
              key={'id'}
            >
              {currencyList.map((currency) => {
                const currencyStr =
                  currency === '' ? t('lobby.modal.pay.allCurrency') : currency
                return (
                  <Select.Option.Tsx
                    key={`${currency}`}
                    customerRender={
                      <div class={style.currencyCell}>
                        {getIcon(currency as string, 'normal')}
                        <span class={style.textCell}>{currencyStr}</span>
                      </div>
                    }
                  >
                    <div class={style.currencyCell}>
                      {getIcon(currency as string, 'large')}
                      <span class={style.text}>{currencyStr}</span>
                    </div>
                  </Select.Option.Tsx>
                )
              })}
            </Select.Tsx>
          </div>
        )}
        <div>{t('lobby.modal.pay.title')}</div>
      </div>
    )
  }

  /**
   * 是否pc界面
   */
  get isWeb() {
    return this.mainStore.isWeb
  }

  handleRecord() {
    Manager.Modal.open('payRecordModal')
  }

  async goBack() {
    await this.baseClose()
    this.resetAllCompIndex()
    this.store.$patch({
      activePlatformCategory: undefined,
      activePlatformMethod: undefined,
      money: undefined
    })
  }

  toCustomerService() {
    GlobalManager.Page.toCustomerService()
  }

  render() {
    const emptyVNode = (
      <div>
        <p>
          {this.t('lobby.modal.pay.payEmpty1')}
          <span onClick={this.toCustomerService}>
            {this.t('lobby.finance.withdraw.maintain.customer')}
          </span>
          {this.t('lobby.modal.pay.payEmpty2')}
        </p>
        {this.store.payListV4Error && (
          <div>
            {this.store.payListV4Error.code}
            {this.store.payListV4Error.requestId && (
              <p>reqeustId: {this.store.payListV4Error.requestId}</p>
            )}
            {this.store.payListV4Error.traceId && (
              <p>traceId: {this.store.payListV4Error.traceId}</p>
            )}
          </div>
        )}
      </div>
    )

    return this.renderModal({
      modalConfig: {
        class: [style.payModal],
        props: {
          closable: !!this.isWeb,
          width: this.isWeb
            ? BusinessUtils.px2rem(800)
            : 'var(--theme-max-width)',
          title: this.renderTitle,
          dialogStyle: {
            top: !this.isWeb ? BusinessUtils.px2rem(90) : 0,
            left: 0,
            position: !this.isWeb ? 'fixed' : 'relative'
          }
        }
      },
      renderContent: () => {
        return (
          <div>
            <CommonLoading
              spinning={this.store.loading}
              class={style.fullScreenLoading}
            />
            <section
              class={this.defaultTabsConfig.length > 0 ? style.root : ''}
            >
              <CommonTabs
                persistedActiveName={''}
                renderList={this.defaultTabsConfig}
                v-model={this.store.rootCategoryIndex}
                onTrigger={this.handleTrigger}
                itemHeight={this.isWeb ? 40 : 72}
                isScrollX={true}
                itemListWrapperClassName={style.payTabsWrapper}
                scopedSlots={{
                  // footer: () => this.renderTabsFooter,
                  default: ({ value }) => {
                    const content = () => {
                      switch (value) {
                        case RootCategoryEnum.ONLINE:
                          return this.sectionWrapper(
                            <online-pay-comp
                              ref={'onlinePayComp'}
                              key="onlinePayComp"
                            />,
                            this.renderFooter
                          )
                        case RootCategoryEnum.TRANSFER:
                          return this.sectionWrapper(
                            <transfer-comp
                              ref={'transferComp'}
                              key="transferComp"
                            />,
                            this.renderFooter
                          )
                        case RootCategoryEnum.CRYPTO:
                          return this.sectionWrapper(
                            <online-pay-comp
                              ref={'cryptoPayComp'}
                              key="cryptoPayComp"
                            />,
                            this.renderFooter
                          )
                        case RootCategoryEnum.ARTIFICIAL:
                          return this.sectionWrapper(
                            <artificial-comp key="artificialComp" />
                          )
                        case RootCategoryEnum.SILVERMERCHANT:
                          return this.sectionWrapper(
                            <silver-merchant key="silverMerchant" />
                          )
                      }
                    }
                    return content()
                  }
                }}
              />
              <PayDetailsModalComp />
            </section>
            {!this.isWeb && (
              <div class={style.goBack} onClick={this.goBack}>
                <icon-sprite sprite={createSvgSprite('comm_icon_fh')} />
              </div>
            )}
            <div class={style.record} onClick={this.handleRecord}>
              <inner-html text={this.t('lobby.modal.pay.record')} />
            </div>

            {!this.defaultTabsConfig.length &&
              !this.store.fetchError &&
              !this.store.loading &&
              this.store.rootCategoryIndex !== RootCategoryEnum.ORDER_LIST && (
                <Empty.Tsx
                  class={style.emptyContent}
                  description={emptyVNode}
                  image={<EmptyImageReport width={300} height={272} />}
                />
              )}

            {/* 超时 */}
            {this.store.fetchError && (
              <div class={style.timeoutError}>
                <NetRetry
                  displayType={DisplayType.ListData}
                  isTimeoutError={true}
                  loading={this.store.loading}
                  onRetry={this.store.initPayConfig}
                ></NetRetry>
              </div>
            )}
          </div>
        )
      }
    })
  }
}
